import { useQuery } from "@tanstack/react-query";
import {
	Activity,
	Database,
	Cpu,
	HardDrive,
	Clock,
	CheckCircle2,
	XCircle,
	AlertTriangle,
	RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

// Fetch functions
async function fetchSystemHealth() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/system/health"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchJobStatus() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/system/jobs"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchMetrics() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/system/metrics"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

const statusColors: Record<string, string> = {
	healthy: "bg-green-100 text-green-700",
	unhealthy: "bg-red-100 text-red-700",
	degraded: "bg-yellow-100 text-yellow-700",
	unknown: "bg-gray-100 text-gray-700",
};

const statusIcons: Record<string, React.ReactNode> = {
	healthy: <CheckCircle2 className="w-5 h-5 text-green-500" />,
	unhealthy: <XCircle className="w-5 h-5 text-red-500" />,
	degraded: <AlertTriangle className="w-5 h-5 text-yellow-500" />,
	unknown: <AlertTriangle className="w-5 h-5 text-gray-500" />,
};

export function SystemHealth() {
	const {
		data: health,
		isLoading: healthLoading,
		refetch: refetchHealth,
	} = useQuery({
		queryKey: ["system-health"],
		queryFn: fetchSystemHealth,
		refetchInterval: 30000, // Refresh every 30 seconds
	});

	const { data: jobs, isLoading: jobsLoading } = useQuery({
		queryKey: ["system-jobs"],
		queryFn: fetchJobStatus,
		refetchInterval: 30000,
	});

	const { data: metrics, isLoading: metricsLoading } = useQuery({
		queryKey: ["system-metrics"],
		queryFn: fetchMetrics,
		refetchInterval: 60000, // Refresh every minute
	});

	const isLoading = healthLoading || jobsLoading || metricsLoading;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">System Health</h1>
					<p className="text-gray-500 mt-1">
						Monitor system status and performance
					</p>
				</div>
				<Button
					variant="outline"
					onClick={() => refetchHealth()}
					disabled={isLoading}
				>
					<RefreshCw
						className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")}
					/>
					Refresh
				</Button>
			</div>

			{/* Overall Status */}
			{health && (
				<Card
					className={cn(
						"border-2",
						health.status === "healthy"
							? "border-green-200"
							: health.status === "degraded"
								? "border-yellow-200"
								: "border-red-200",
					)}
				>
					<CardContent className="p-6">
						<div className="flex items-center justify-between">
							<div className="flex items-center gap-4">
								{statusIcons[health.status]}
								<div>
									<h2 className="text-xl font-bold capitalize">
										System {health.status}
									</h2>
									<p className="text-sm text-gray-500">
										Response time: {health.responseTime}ms
									</p>
								</div>
							</div>
							<div className="text-right">
								<p className="text-sm text-gray-500">
									Last checked: {new Date(health.timestamp).toLocaleTimeString()}
								</p>
								<p className="text-sm text-gray-500">
									Uptime: {metrics?.uptime?.formatted || "-"}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Service Status */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
				{/* MongoDB */}
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="flex items-center gap-2 text-lg">
							<Database className="w-5 h-5" />
							MongoDB
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-center justify-between">
							<Badge
								className={
									statusColors[health?.services?.mongodb?.status || "unknown"]
								}
							>
								{health?.services?.mongodb?.status || "unknown"}
							</Badge>
							{statusIcons[health?.services?.mongodb?.status || "unknown"]}
						</div>
					</CardContent>
				</Card>

				{/* Redis */}
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="flex items-center gap-2 text-lg">
							<Activity className="w-5 h-5" />
							Redis
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-center justify-between">
							<Badge
								className={
									statusColors[health?.services?.redis?.status || "unknown"]
								}
							>
								{health?.services?.redis?.status || "unknown"}
							</Badge>
							{statusIcons[health?.services?.redis?.status || "unknown"]}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Memory & CPU */}
			{metrics && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<HardDrive className="w-5 h-5" />
								Memory Usage
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-3">
								<div className="flex justify-between">
									<span className="text-gray-600">Heap Used</span>
									<span className="font-medium">{metrics.memory.heapUsed} MB</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">Heap Total</span>
									<span className="font-medium">{metrics.memory.heapTotal} MB</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">RSS</span>
									<span className="font-medium">{metrics.memory.rss} MB</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">External</span>
									<span className="font-medium">{metrics.memory.external} MB</span>
								</div>
								<div className="mt-3 pt-3 border-t">
									<div className="w-full bg-gray-200 rounded-full h-2.5">
										<div
											className="bg-blue-600 h-2.5 rounded-full"
											style={{
												width: `${Math.min((metrics.memory.heapUsed / metrics.memory.heapTotal) * 100, 100)}%`,
											}}
										/>
									</div>
									<p className="text-xs text-gray-500 mt-1 text-right">
										{Math.round(
											(metrics.memory.heapUsed / metrics.memory.heapTotal) * 100,
										)}
										% heap used
									</p>
								</div>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Clock className="w-5 h-5" />
								Uptime & Process
							</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="space-y-3">
								<div className="flex justify-between">
									<span className="text-gray-600">Uptime</span>
									<span className="font-medium">
										{metrics.uptime?.formatted || "-"}
									</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">Environment</span>
									<span className="font-medium">{health?.environment || "-"}</span>
								</div>
								<div className="flex justify-between">
									<span className="text-gray-600">Version</span>
									<span className="font-medium">{health?.version || "-"}</span>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			)}

			{/* Job Queues */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center justify-between">
						<span className="flex items-center gap-2">
							<Cpu className="w-5 h-5" />
							Job Queues
						</span>
						<a
							href="/admin/queues"
							target="_blank"
							rel="noopener noreferrer"
							className="text-sm font-normal text-blue-600 hover:underline"
						>
							Open Bull Board
						</a>
					</CardTitle>
				</CardHeader>
				<CardContent>
					{jobs?.workers ? (
						<div className="space-y-4">
							{Object.entries(jobs.workers).map(
								([name, status]: [string, any]) => (
									<div
										key={name}
										className="flex items-center justify-between py-2 border-b last:border-0"
									>
										<div>
											<p className="font-medium">{name}</p>
											<p className="text-sm text-gray-500">
												Worker: {status.running ? "Running" : "Stopped"}
											</p>
										</div>
										<Badge
											className={
												status.running
													? "bg-green-100 text-green-700"
													: "bg-gray-100 text-gray-700"
											}
										>
											{status.running ? "Active" : "Inactive"}
										</Badge>
									</div>
								),
							)}
						</div>
					) : (
						<p className="text-gray-500 text-center py-4">
							No job queue data available
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
